import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToTop } from "../lib/scroll";
import { PRIMARY_NAV, FOOTER_NAV } from "../lib/nav";
import { CywizMark } from "./CywizMark";
import { CtaButton } from "./Shared";

import { useLang } from "../lib/i18n";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const labelFor = (item, t) =>
  item.labelKey === null ? <CywizMark /> : t.nav[item.labelKey];

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pendingRef = useRef(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const reduce = useReducedMotion();

  const LINKS = PRIMARY_NAV.map((item) => ({
    ...item,
    label: labelFor(item, t),
  }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Échap + piège de focus quand le menu mobile est ouvert
  useEffect(() => {
    if (!open) return;

    const panel = menuRef.current;
    const getFocusable = () =>
      panel ? Array.from(panel.querySelectorAll(FOCUSABLE)) : [];

    const close = () => {
      setOpen(false);
      requestAnimationFrame(() => toggleRef.current?.focus());
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab" || !panel) return;

      const items = getFocusable();
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    requestAnimationFrame(() => {
      const items = getFocusable();
      (items[0] || toggleRef.current)?.focus();
    });

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Depuis une page intérieure, une ancre doit d'abord ramener sur la home.
  const goTo = (link, e) => {
    e?.preventDefault();
    if (open) {
      pendingRef.current = link;
      setOpen(false);
      return;
    }
    if (pathname === "/") scrollToId(link.href);
    else navigate({ pathname: "/", hash: link.href.replace(/^#/, "") });
  };

  const LangToggle = ({ className = "" }) => (
    <button
      type="button"
      data-testid="lang-toggle"
      onClick={toggle}
      aria-label={t.common.switchLang}
      className={`inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] ${className}`}
    >
      <span lang="fr" className={lang === "fr" ? "text-cream" : "text-dim"} aria-current={lang === "fr" ? "true" : undefined}>
        FR
      </span>
      <span className="text-dim" aria-hidden>
        /
      </span>
      <span lang="en" className={lang === "en" ? "text-cream" : "text-dim"} aria-current={lang === "en" ? "true" : undefined}>
        EN
      </span>
    </button>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-[9000] transition-all duration-500 ${
        scrolled || pathname !== "/" ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="max-w-site mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-home"
          onClick={() => pathname === "/" && scrollToTop()}
          className="flex items-center gap-3"
          aria-label={t.common.home}
        >
          <img src="/images/aelvir_mark.webp" alt="" className="h-12 w-auto" />
          <img
            src="/images/mot_aelvir.webp"
            alt={t.common.logoAlt}
            className="h-5 w-auto md:h-6"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-7" aria-label={t.footer.nav}>
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-${l.key}`}
              onClick={(e) => goTo(l, e)}
              className="group relative whitespace-nowrap text-xs uppercase tracking-[0.2em] transition-colors duration-300 text-warm hover:text-cream"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-brand transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-6">
          <LangToggle className="hover:opacity-80 transition-opacity" />
          <CtaButton
            variant="header"
            data-testid="header-cta"
            href="#contact"
            onClick={(e) => goTo({ href: "#contact" }, e)}
          >
            {t.contactCta}
          </CtaButton>
        </div>

        <div className="xl:hidden flex items-center gap-4">
          <LangToggle />
          <CtaButton
            variant="header"
            data-testid="header-cta-mobile"
            href="#contact"
            onClick={(e) => goTo({ href: "#contact" }, e)}
            className="hidden sm:inline-flex px-4 py-2"
          >
            {t.contactCta}
          </CtaButton>
          <button
            ref={toggleRef}
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-cream"
            aria-label={open ? t.common.closeMenu : t.common.openMenu}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X size={26} aria-hidden /> : <Menu size={26} aria-hidden />}
          </button>
        </div>
      </div>

      <AnimatePresence
        onExitComplete={() => {
          if (!pendingRef.current) return;
          const link = pendingRef.current;
          pendingRef.current = null;
          goTo(link);
        }}
      >
        {open && (
          <motion.nav
            ref={menuRef}
            id="mobile-nav"
            data-testid="mobile-nav"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.35 }}
            className="xl:hidden overflow-hidden bg-section/95 backdrop-blur-xl border-b border-copper-light/10"
            aria-label={t.footer.nav}
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {FOOTER_NAV.map((item) => {
                const l = { ...item, label: labelFor(item, t) };
                return (
                  <a
                    key={l.key}
                    href={l.href}
                    onClick={(e) => goTo(l, e)}
                    className="text-left text-xs uppercase tracking-[0.2em] text-warm hover:text-cream transition-colors duration-300"
                  >
                    {l.label}
                  </a>
                );
              })}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
